import { captureException } from "@sentry/react";
import confettiModuleUrl from "canvas-confetti/dist/confetti.module.mjs?url";

type FruitConfettiOptions = {
  origin?: { x: number; y: number };
  bananaOnly?: boolean;
  scalar?: number;
  zIndex?: number;
};

/** Optional celebration code loads after the reward is visible. */
export function startFruitConfettiAnimation({
  origin,
  bananaOnly = false,
  scalar = 3,
  zIndex = 1400,
}: FruitConfettiOptions = {}): () => void {
  let cancelled = false;
  const timers: ReturnType<typeof setTimeout>[] = [];

  // This standalone optional asset must not enter Vite's mandatory-chunk reload path.
  const confettiModuleLoadPromise = import(/* @vite-ignore */ confettiModuleUrl) as Promise<{ default: typeof import("canvas-confetti") }>;
  void confettiModuleLoadPromise.then(({ default: confetti }) => {
    if (cancelled) return;
    const banana = confetti.shapeFromText({ text: "🍌", scalar });
    const shapes = bananaOnly ? [banana] : [
      confetti.shapeFromText({ text: "🍍", scalar }),
      banana,
      confetti.shapeFromText({ text: "🥥", scalar }),
    ];
    const defaults = {
      spread: 240,
      ticks: bananaOnly ? 190 : 225,
      gravity: 0,
      decay: 0.97,
      startVelocity: 9,
      shapes,
      scalar,
      origin,
      zIndex,
    };
    const shoot = () => {
      confetti({ ...defaults, particleCount: bananaOnly ? 25 : 30 });
      confetti({ ...defaults, particleCount: 5, flat: true });
      confetti({ ...defaults, particleCount: bananaOnly ? 0 : 25, scalar: scalar / 2, shapes: ["circle"] });
    };
    for (const delay of [0, 100, 200]) timers.push(setTimeout(shoot, delay));
  }).catch((error) => {
    // A missing optional animation must never prevent opening/closing a reward.
    captureException(error, { tags: { feature: "gamification", operation: "fruit-confetti" } });
  });

  return () => {
    cancelled = true;
    timers.forEach(clearTimeout);
  };
}
